import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Mail, Send, Clock, Bot, CheckCircle, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FAQChatbot from '../components/FAQChatbot';
import SupportTicketForm from '../components/SupportTicketForm';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../App';



const ContactPage = ({ user, onLogout, refreshUserData }) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initialCategory = searchParams.get('category') || 'autre';
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    subject: '',
    message: '',
    category: initialCategory
  });
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isTicketFormOpen, setIsTicketFormOpen] = useState(false);
  
  // Scroll to form if hash is present
  useEffect(() => {
    if (location.hash === '#contact-form') {
      setTimeout(() => {
        const form = document.getElementById('contact-form');
        if (form) {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [location.hash]);
  
  // Update subject based on category
  useEffect(() => {
    const subjectMap = {
      'nouvelle_billeterie': 'Suggestion: Ajouter une nouvelle plateforme de billets',
      'suggestion_amelioration': 'Suggestion: Amélioration de Middleman'
    };
    if (subjectMap[initialCategory]) {
      setFormData(prev => ({ 
        ...prev, 
        subject: prev.subject || subjectMap[initialCategory] 
      }));
    }
  }, [initialCategory]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/support/tickets`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone || null,
        subject: formData.subject,
        description: formData.message,
        category: formData.category,
        priority: '48h',
        source: 'form'
      });
      
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success(t('contact.successMessage'));
    } catch (error) {
      toast.error(t('contact.errorSending'));
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setTicketId(null);
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: '',
      subject: '',
      message: '',
      category: 'autre'
    });
  };

  const openMessenger = () => {
    window.open('https://m.me/466226189896489', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      {/* Spacer for fixed navbar */}
      <div className="h-20"></div>

      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('contact.pageTitle')}</h1>
          <p className="text-xl text-brand-100">{t('contact.pageSubtitle')}</p>
        </div>
      </div>

      {/* Support Options */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          
          {/* 3 Support Options Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            
            {/* Option 1: Chatbot */}
            <Card className="hover:shadow-xl transition-shadow border-2 hover:border-brand-500 cursor-pointer group"
                  onClick={() => setIsChatbotOpen(true)}
                  data-testid="support-option-chatbot">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-200 transition-colors">
                  <Bot className="h-8 w-8 text-brand-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contact.chatbotTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('contact.chatbotDesc')}</p>
                <div className="flex items-center justify-center gap-2 text-green-600 font-semibold">
                  <Clock className="h-4 w-4" />
                  <span>{t('contact.chatbotResponse')}</span>
                </div>
                <Button className="mt-4 w-full bg-brand-600 hover:bg-brand-700">
                  {t('contact.startChat')}
                </Button>
              </CardContent>
            </Card>

            {/* Option 2: Messenger */}
            <Card className="hover:shadow-xl transition-shadow border-2 hover:border-brand-500 cursor-pointer group"
                  onClick={openMessenger}
                  data-testid="support-option-messenger">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-200 transition-colors">
                  <svg className="h-8 w-8 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.936 1.444 5.544 3.746 7.256v3.501l3.373-1.851c.9.25 1.851.384 2.881.384 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.093 12.441l-2.545-2.72-4.968 2.72 5.467-5.803 2.609 2.72 4.905-2.72-5.468 5.803z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contact.messengerTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('contact.messengerDesc')}</p>
                <div className="flex items-center justify-center gap-2 text-brand-600 font-semibold">
                  <Clock className="h-4 w-4" />
                  <span>{t('contact.messengerResponse')}</span>
                </div>
                <Button className="mt-4 w-full bg-brand-600 hover:bg-brand-700">
                  {t('contact.openMessenger')}
                </Button>
              </CardContent>
            </Card>

            {/* Option 3: Ticket Form */}
            <Card className="hover:shadow-xl transition-shadow border-2 hover:border-brand-500 group"
                  data-testid="support-option-ticket">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-200 transition-colors">
                  <Ticket className="h-8 w-8 text-brand-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contact.ticketTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('contact.ticketDesc')}</p>
                <div className="flex items-center justify-center gap-2 text-brand-600 font-semibold">
                  <Clock className="h-4 w-4" />
                  <span>{t('contact.ticketResponse')}</span>
                </div>
                <Button 
                  className="mt-4 w-full bg-brand-600 hover:bg-brand-700"
                  onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('contact.createTicket')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Divider */}
          <div className="relative my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-50 text-gray-500">{t('contact.orFillForm')}</span>
            </div>
          </div>

          {/* Contact Form Section */}
          <div id="contact-form" className="max-w-2xl mx-auto">
            <Card className="shadow-lg" id="contact-form">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('contact.formTitle')}</h2>
                  <p className="text-gray-600">{t('contact.formSubtitle')}</p>
                </div>

                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('contact.successTitle')}</h3>
                    <div className="bg-gray-50 rounded-lg p-4 mb-4 max-w-xs mx-auto">
                      <p className="text-sm text-gray-500">{t('contact.ticketNumber')}</p>
                      <p className="font-mono text-lg font-semibold text-brand-600">
                        #{ticketId?.slice(0, 8).toUpperCase()}
                      </p>
                    </div>
                    <p className="text-gray-600 mb-4">{t('contact.successMessage')}</p>
                    <Button onClick={resetForm} variant="outline">
                      {t('contact.newMessage')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-ticket-form">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.yourName')} *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.yourName')}
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.yourEmail')} *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="votre@email.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.phone')}
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 514 123 4567"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.category')}
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          data-testid="contact-category"
                        >
                          <option value="support">Support technique</option>
                          <option value="paiement">Paiement / Transaction</option>
                          <option value="info_generale">Informations générales</option>
                          <option value="nouvelle_billeterie">Suggérer une nouvelle billeterie</option>
                          <option value="suggestion_amelioration">Suggérer une amélioration</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.subject')} *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.subject')}
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.message')} *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        placeholder={t('contact.messagePlaceholder')}
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-brand-600 hover:bg-brand-700"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          {t('contact.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          {t('contact.send')}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Email Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              {t('common.email')}: <a href="mailto:support@transfertdebillets.ca" className="text-brand-600 hover:underline font-semibold">support@transfertdebillets.ca</a>
            </p>
          </div>
        </div>
      </div>

      {/* Chatbot Modal */}
      {isChatbotOpen && (
        <FAQChatbot 
          isOpen={isChatbotOpen} 
          onClose={() => setIsChatbotOpen(false)} 
          user={user}
        />
      )}

      {/* Ticket Form Modal */}
      {isTicketFormOpen && (
        <SupportTicketForm
          isOpen={isTicketFormOpen}
          onClose={() => setIsTicketFormOpen(false)}
          user={user}
        />
      )}

      <Footer />
    </div>
  );
};

export default ContactPage;
